import type {ExtraInfo} from './data/types';

import {EventInfo_EventFragment} from './__generated__/Event.graphql';
import formatHourRange from '../../time/formatHourRange';
import useAppTheme from '../design_system/hooks/useAppTheme';
import useMediaQuery from '../design_system/hooks/useMediaQuery';
import useMediaQueryGetter, {
  MediaQuery,
} from '../design_system/hooks/useMediaQueryGetter';
import {ContentColor} from '../design_system/theme/useContentPalette';
import Typography, {Size} from '../design_system/typography/Typography';
import Flex from '../layout/flex/Flex';
import MapAddress from '../location/MapAddress';
import SquareMonthDate from '../time/SquareMonthDate';

import {css} from '@emotion/react';
import {formatInTimeZone} from 'date-fns-tz';
import React from 'react';

export interface Props {
  readonly data: EventInfo_EventFragment;
  readonly extraData: Maybe<ExtraInfo>;
}

export default function EventInfo(props: Props): JSX.Element {
  const {data, extraData} = props;
  const theme = useAppTheme();
  const getMediaQuery = useMediaQueryGetter();
  const startDate = new Date(data.startTimeUTCSeconds * 1000);
  const borderStyle = `2px solid ${theme.palettes.background.neutral.disabled}`;
  const tags = extraData?.tags ?? [];

  return (
    <Flex
      sx={css({
        flexDirection: 'column',
        gap: '20px',
        paddingBottom: '20px',
        width: '100%',
        alignItems: 'flex-start',
        borderBottom: borderStyle,

        [getMediaQuery(MediaQuery.LargeAndExtraLarge)]: {
          borderRight: borderStyle,
          borderBottom: 0,
          paddingBottom: 0,
        },
      })}
    >
      <Flex
        sx={css({
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '20px',
          alignItems: 'flex-start',
          width: '100%',
        })}
      >
        <Flex
          sx={css({
            gap: '20px',
            flexGrow: 1,
          })}
        >
          <SquareMonthDate
            size={80}
            timestampUTCSeconds={data.startTimeUTCSeconds}
          />
          <Flex
            direction="column"
            sx={css({
              alignItems: 'flex-start',
            })}
          >
            <Typography variant="div">
              {formatInTimeZone(startDate, 'Asia/Tokyo', 'EEEE')}
            </Typography>

            <Typography
              variant="div"
              weight={700}
            >
              {formatHourRange({
                durationSeconds: data.durationSeconds,
                startTime: data.startTimeUTCSeconds,
              })}
            </Typography>
          </Flex>
        </Flex>

        <div
          css={css({
            flexGrow: 1,
          })}
        >
          <MapAddress address={data.venue} />
        </div>
      </Flex>
      <Flex
        sx={css({
          display: tags.length === 0 ? 'none' : 'flex',
          alignContent: 'flex-start',
          columnGap: theme.spacing.x8,
          flexWrap: 'wrap',
          rowGap: theme.spacing.x4,
          width: 120,

          [useMediaQuery(MediaQuery.SmallScreen)]: {
            gridColumn: '1 / span 2',
            width: 'auto',
          },
        })}
      >
        {extraData?.tags?.map((tag, i) => {
          return (
            <span key={i}>
              <Typography
                color={ContentColor.PRIMARY_SUBDUED}
                css={css({
                  backgroundColor:
                    theme.palettes.background.neutralExtraSubdued.disabled,
                  borderRadius: '3px',
                  paddingBottom: theme.spacing.x4,
                  paddingLeft: theme.spacing.x8,
                  paddingRight: theme.spacing.x8,
                  paddingTop: theme.spacing.x4,
                  whiteSpace: 'nowrap',
                })}
                size={Size.XS}
                variant="span"
              >
                {tag.label}
              </Typography>
            </span>
          );
        })}
      </Flex>
    </Flex>
  );
}
